import { useEffect, useState } from "react";
import { treatmentsApi } from "../../services/treatmentsApi";
import { exportApi } from "../../services/exportApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { DataTable } from "../../components/common/DataTable";
import { Field } from "../../components/common/Field";
import { TERMS } from "../../services/terms";
import { formatDateOnly, withdrawalNote } from "../../services/hiveActions";

export function TreatmentsTab({ hive, treatments, onLogged }) {
  const { notify } = useToast();
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState({ treatment_id: "", application_date: new Date().toISOString().slice(0, 10), dosage: "" });
  const [exporting, setExporting] = useState(false);
  /*
   * ADDING ONE THE CATALOGUE DOES NOT HAVE (#398).
   *
   * Shipped on mobile first and not here, which is precisely the gap
   * platformParity.test.js exists to stop -- a feature built on one
   * platform with the other half nobody noticed missing.
   */
  const [adding, setAdding] = useState(false);
  const [newTreatment, setNewTreatment] = useState({
    name: "", treatment_type: "Mite", withdrawal_period_days: "",
  });

  async function addTreatment() {
    const name = newTreatment.name.trim();
    if (!name) return notify("Give the treatment a name", "warning");
    // Deliberately NOT defaulted: an empty box and a considered zero
    // must not reach the API as the same thing.
    if (!/^\d+$/.test(newTreatment.withdrawal_period_days)) {
      return notify("Enter the withdrawal period in days — 0 if the product has none", "warning");
    }
    try {
      const created = await treatmentsApi.createTreatment({
        name,
        treatment_type: newTreatment.treatment_type,
        withdrawal_period_days: Number(newTreatment.withdrawal_period_days),
      });
      const r = await treatmentsApi.listCatalog();
      setCatalog(r.treatments);
      // Selected, not merely created -- otherwise somebody adds a
      // treatment mid-job and has to find it again in a list they just
      // added to (#249's point about the varietal picker).
      if (created?.treatment_id) {
        setForm((f) => ({ ...f, treatment_id: String(created.treatment_id) }));
      }
      setAdding(false);
      setNewTreatment({ name: "", treatment_type: "Mite", withdrawal_period_days: "" });
      // `created` distinguishes a new one from a name already on the
      // list, so "Added Apiguard" is not a small lie the second time.
      notify(created?.created ? `Added ${name}` : `${name} was already on your list`, "success");
    } catch (err) {
      notify(err.apiMessage || "Could not add that treatment", "danger");
    }
  }

  useEffect(() => {
    treatmentsApi.listCatalog().then((r) => setCatalog(r.treatments));
  }, []);

  async function submit() {
    if (!form.treatment_id) return notify("Choose a treatment", "warning");
    try {
      await treatmentsApi.log(hive.hive_id, { treatment_id: Number(form.treatment_id), application_date: form.application_date, dosage: form.dosage || undefined });
      notify("Treatment logged", "success");
      onLogged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not log treatment", "danger");
    }
  }

  async function exportCsv() {
    setExporting(true);
    try {
      await exportApi.treatmentsCsv();
      notify("Treatments exported", "success");
    } catch (err) {
      notify(err.apiMessage || "Export failed", "danger");
    } finally {
      setExporting(false);
    }
  }

  const rotationGroups = [...new Set(catalog.map((t) => t.rotation_group).filter(Boolean))];

  return (
    <div>
      {/* Logging comes FIRST (reported 27 Aug). You open this tab to
          record something you just did at the hive; the history is
          reference you consult afterwards. Putting the reading above
          the doing makes the common case scroll past the rare one. */}
      <Card title="Log Treatment">
        {/* NO SEPARATE "what is a withdrawal period" EXPLAINER HERE, and
            that is a decision rather than an omission.

            This tab already has a "Withdrawal period (days)" field with
            the "?" on it, in the add-a-treatment form. A second control
            with the same label put two of them on one screen -- one an
            input, one an explanation -- which is worse than the gap it
            filled.

            The phone needs it because its picker shows the phrase; here
            each option says the wait in plain words ("wait 14 days before
            taking honey"), which needs no glossary. Same fact, and the
            term of art is explained where it is actually typed. */}

        <div style={{ display: "grid", gap: "var(--space-3)", maxWidth: 360 }}>
          <select id="treatments-tab-treatment-id" name="treatment-id" value={form.treatment_id} onChange={(e) => setForm((f) => ({ ...f, treatment_id: e.target.value }))} className="touch-target">
            <option value="">Choose treatment…</option>
            {/* THE WAIT, ON THE OPTION ITSELF (#404 follow-up). A <select>
                has no room for a second line the way mobile's picker
                does, so the note joins the name -- which is fine here
                because an option is read once, at the moment of choosing,
                and that is exactly when the number could change which
                product goes in the hive. Same wording as the phone. */}
            {catalog.map((t) => (
              <option key={t.treatment_id} value={t.treatment_id}>
                {`${t.name} — ${withdrawalNote(t).toLowerCase()}`}
              </option>
            ))}
          </select>
          <input id="treatments-tab-application-date" name="application-date" type="date" value={form.application_date} onChange={(e) => setForm((f) => ({ ...f, application_date: e.target.value }))} className="touch-target" />
          <input id="treatments-tab-dosage" name="dosage" placeholder="Dosage (optional)" value={form.dosage} onChange={(e) => setForm((f) => ({ ...f, dosage: e.target.value }))} className="touch-target" />
          <Button onClick={submit}>Log Treatment</Button>
        </div>

        {adding ? (
          <div style={{ display: "grid", gap: "var(--space-3)", maxWidth: 360, marginTop: "var(--space-4)" }}>
            <Field label="Treatment name" required>
              {(id, aria) => (
                <input id={id} {...aria} className="touch-target"
                  placeholder="As it is on the label"
                  value={newTreatment.name}
                  onChange={(e) => setNewTreatment((t) => ({ ...t, name: e.target.value }))} />
              )}
            </Field>
            <Field label="What it treats" required>
              {(id, aria) => (
                <select id={id} {...aria} className="touch-target"
                  value={newTreatment.treatment_type}
                  onChange={(e) => setNewTreatment((t) => ({ ...t, treatment_type: e.target.value }))}>
                  {["Mite", "Foulbrood", "Nosema", "Other"].map((v) => <option key={v}>{v}</option>)}
                </select>
              )}
            </Field>
            {/* THE FIELD THIS FORM EXISTS FOR, under its real name and
                with the explanation behind a "?" rather than always on
                screen. The term is what is PRINTED ON THE BOTTLE, so a
                friendlier label would leave somebody unable to match the
                two -- and the explanation is a paragraph, not a caption. */}
            <Field label="Withdrawal period (days)" required help={TERMS.withdrawalPeriod}>
              {(id, aria) => (
                <input id={id} {...aria} type="number" min="0" max="365" className="touch-target"
                  placeholder="e.g. 14"
                  value={newTreatment.withdrawal_period_days}
                  onChange={(e) => setNewTreatment((t) => ({ ...t, withdrawal_period_days: e.target.value }))} />
              )}
            </Field>
            <div style={{ display: "flex", gap: "var(--space-2)" }}>
              <Button onClick={addTreatment}>Add Treatment</Button>
              <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setAdding(true)}
            style={{ marginTop: "var(--space-3)" }}>
            + Add a treatment
          </Button>
        )}
      </Card>

      <Card
        title="History & Next Due"
        className="section-spacing"
        actions={<Button size="sm" variant="secondary" onClick={exportCsv} disabled={exporting}>{exporting ? "Exporting…" : "Export CSV"}</Button>}
      >
        <DataTable
          rows={treatments}
          rowKey="log_id"
          emptyTitle="No treatments logged"
          emptyDescription="Mite and disease treatments for this colony, with the product and dates used."
          columns={[
            { key: "application_date", header: "Applied", render: (r) => formatDateOnly(r.application_date) },
            { key: "treatment_id", header: "Treatment", render: (r) => catalog.find((c) => c.treatment_id === r.treatment_id)?.name || r.treatment_id },
            { key: "next_application_date", header: "Next Due", render: (r) => (formatDateOnly(r.next_application_date) || "—") },
            { key: "efficacy_rating", header: "Efficacy" },
          ]}
        />
        {rotationGroups.length > 0 && (
          <p style={{ marginTop: "var(--space-3)", color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
            Rotation groups in your catalog: {rotationGroups.join(", ")} — rotate groups between applications to slow resistance.
          </p>
        )}
      </Card>

    </div>
  );
}
